import * as React from 'react';
import type { PromotionTarget } from '@/types/api';
import { ENVIRONMENT, PROMOTION_TARGET, errorMessage } from '@/lib/copy';
import { formatClock, formatDelta, pluralize } from '@/lib/format';
import { useDebounced, useElementHeight } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Empty } from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { Panel, PanelHeader } from '@/components/ui/panel';
import { Skeleton } from '@/components/ui/skeleton';
import { useAggregatedDiff } from './queries';
import { DiffRowView } from './diff-rows';
import { FileTree } from './file-tree';
import { buildRows, groupRepos, rowIndexAt } from './rows';
import { useHighlighter, type HighlightItem } from './use-highlighter';

/* ---------------------------------------------------------------------------
   THE AGGREGATED MULTI-REPO DIFF VIEWER.

   This is the screen an expert spends most of a review inside, and the one the
   whole promotion decision rests on. Four properties are non-negotiable, and
   each is a decision that had to be made somewhere:

     1. THE DIFF IS COMPUTED SERVER-SIDE. Structured hunks arrive from
        GET /expert/chats/{id}/diff with line numbers already resolved per side.
        Nothing here parses patch text and nothing here compares two blobs.
        Shipping raw patches for the browser to diff is how a review screen
        becomes the slowest page in a product.

     2. IT IS DIFFED AGAINST THE PROMOTION TARGET, not against the start of the
        chat. So the target selector re-fetches: a staging diff and a production
        diff are different questions, and after a staging merge the production
        answer changes underneath. What is on screen is exactly what the button
        below it would merge.

     3. ROWS ARE VIRTUALISED over an exact offset table. A large multi-repo
        change is tens of thousands of rows; mounting them would lock the tab,
        and estimating their heights would drift a file header away from its
        lines — which on this screen means approving a change that was never
        read.

     4. HIGHLIGHTING IS OFF THE MAIN THREAD, requested per visible window and
        cached. A row with no tokens yet paints as plain mono ink, so scrolling
        never waits on a tokeniser.

   The per-repo grouping is in PROMOTION ORDER — the order the merges will
   actually run in — and unchanged repos are drawn rather than hidden, because
   "this repo was considered and had nothing in it" is a different fact from
   "this repo was not looked at", and only one of them is safe to promote on.
--------------------------------------------------------------------------- */

/** Pixels of rows built above and below the window. Two screens is enough that a
    fast flick lands on painted rows rather than on a blank pane. */
const OVERSCAN = 600;

export function DiffViewer({
  chatId,
  target,
  onTargetChange,
  className,
}: {
  chatId: number;
  target: PromotionTarget;
  onTargetChange: (target: PromotionTarget) => void;
  className?: string;
}) {
  // `repo_id` narrows to one repo AND lifts its truncation budget — the
  // documented escape from an aggregate that was cut. It is a separate fetch,
  // not a filter, for exactly that reason.
  const [isolatedRepo, setIsolatedRepo] = React.useState<number | null>(null);
  const [filterInput, setFilterInput] = React.useState('');
  const filter = useDebounced(filterInput, 180);
  const [selectedFile, setSelectedFile] = React.useState<string | null>(null);
  const [onlyFile, setOnlyFile] = React.useState<string | null>(null);
  const [collapsedRepos, setCollapsedRepos] = React.useState<ReadonlySet<number>>(() => new Set<number>());

  const diff = useAggregatedDiff(chatId, target, isolatedRepo);
  const highlighter = useHighlighter();

  const portRef = React.useRef<HTMLDivElement>(null);
  const portHeight = useElementHeight(portRef);
  const [portWidth, setPortWidth] = React.useState(0);
  const [scrollTop, setScrollTop] = React.useState(0);

  // A new diff invalidates every cached token: the same row key can name
  // different content once the underlying SHAs moved.
  const dataToken = diff.data?.computed_at ?? null;
  React.useEffect(() => {
    highlighter.clear();
  }, [dataToken, target, isolatedRepo, highlighter]);

  const repos = React.useMemo(() => groupRepos(diff.data), [diff.data]);

  const model = React.useMemo(
    () => buildRows(diff.data, { filter, collapsedRepos, onlyFile }),
    [diff.data, filter, collapsedRepos, onlyFile],
  );

  React.useEffect(() => {
    const el = portRef.current;
    if (!el) return;
    const measure = () => setPortWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Scroll position is read from the DOM in a frame rather than on every scroll
  // event: a trackpad fires these faster than React can render, and the pane
  // only needs the value once per painted frame.
  const frameRef = React.useRef<number | null>(null);
  const onScroll = React.useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      setScrollTop(portRef.current?.scrollTop ?? 0);
    });
  }, []);
  React.useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  const first = rowIndexAt(model.offsets, Math.max(0, scrollTop - OVERSCAN));
  const last = Math.min(
    model.rows.length,
    rowIndexAt(model.offsets, scrollTop + (portHeight || 640) + OVERSCAN) + 1,
  );

  const visible = model.rows.slice(first, last);
  const padTop = model.offsets[first] ?? 0;
  const padBottom = Math.max(0, model.totalHeight - (model.offsets[last] ?? model.totalHeight));

  // Ask the worker for exactly the window that is about to paint.
  React.useEffect(() => {
    const batch: HighlightItem[] = [];
    for (const row of visible) {
      if (row.kind !== 'line') continue;
      if (!row.line.content) continue;
      batch.push({ key: row.key, language: row.language, content: row.line.content });
    }
    if (batch.length) highlighter.request(batch);
  }, [visible, highlighter]);

  /**
   * Jump the pane to a file the reviewer picked in the tree.
   *
   * While a file is isolated, picking another one moves the isolation rather
   * than leaving the pane on the old file with the tree highlighting the new —
   * two controls disagreeing about which file is on screen is worse than either
   * behaviour on its own.
   */
  const scrollToFile = React.useCallback(
    (key: string | null) => {
      setSelectedFile(key);
      if (onlyFile !== null) {
        setOnlyFile(key);
        portRef.current?.scrollTo({ top: 0 });
        return;
      }
      if (!key) return;
      const index = model.fileAnchors.get(key);
      if (index === undefined) return;
      const y = model.offsets[index] ?? 0;
      portRef.current?.scrollTo({ top: Math.max(0, y - 4), behavior: 'auto' });
    },
    [model, onlyFile],
  );

  const toggleRepo = React.useCallback((repoId: number) => {
    setCollapsedRepos((previous) => {
      const next = new Set(previous);
      if (next.has(repoId)) next.delete(repoId);
      else next.add(repoId);
      return next;
    });
  }, []);

  const totals = diff.data?.totals;
  const changedRepos = repos.filter((r) => r.changed).length;

  return (
    <Panel className={cn('overflow-hidden', className)}>
      <PanelHeader
        title="Aggregated diff"
        meta={
          totals
            ? `${pluralize(totals.files_changed, 'file')} · ${formatDelta(totals.additions, totals.deletions)} · ${changedRepos} of ${pluralize(repos.length, 'repo')} changed`
            : undefined
        }
        actions={
          <>
            <TargetSelector target={target} onChange={onTargetChange} />
            <Button
              size="sm"
              variant="ghost"
              loading={diff.isFetching}
              onClick={() => void diff.refetch()}
              title="Re-compute the diff. Cheap to press; the mirrors are re-fetched server-side."
            >
              Recompute
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-rule bg-ground px-3 py-2">
        <Input
          mono
          value={filterInput}
          onChange={(event) => setFilterInput(event.target.value)}
          placeholder="filter paths"
          aria-label="Filter changed files by path"
          className="h-7 w-[min(20rem,100%)]"
        />
        {model.hiddenFileCount > 0 ? (
          <span className="font-mono text-tiny text-warn">
            {model.hiddenFileCount} file{model.hiddenFileCount === 1 ? '' : 's'} hidden by the filter
          </span>
        ) : null}

        {selectedFile ? (
          <Button
            size="sm"
            variant={onlyFile ? 'primary' : 'secondary'}
            onClick={() => setOnlyFile(onlyFile ? null : selectedFile)}
            title="Show only the selected file"
          >
            {onlyFile ? 'Showing one file' : 'Isolate file'}
          </Button>
        ) : null}

        {isolatedRepo !== null ? (
          <Button size="sm" variant="secondary" onClick={() => setIsolatedRepo(null)}>
            Back to all repos
          </Button>
        ) : null}

        <span className="ml-auto font-mono text-tiny text-ink-3">
          {diff.data?.computed_at ? `computed ${formatClock(diff.data.computed_at)}` : ''}
        </span>
      </div>

      {diff.isLoading ? (
        <DiffSkeleton />
      ) : diff.isError ? (
        <Empty
          title="The diff could not be computed"
          body={errorMessage(diff.error.code, diff.error.message)}
          action={
            <Button size="sm" variant="secondary" onClick={() => void diff.refetch()}>
              Retry
            </Button>
          }
        />
      ) : model.rows.length === 0 ? (
        <Empty
          title={filter ? 'No file matches that filter' : 'Nothing to review against this target'}
          body={
            filter
              ? 'Clear the filter to see the whole change.'
              : `Every repo is already up to date with ${ENVIRONMENT[target].label.toLowerCase()}. There is nothing a promotion would merge.`
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)]">
          <div className="max-h-[30vh] overflow-y-auto border-b border-rule lg:max-h-[72vh] lg:border-b-0 lg:border-r">
            <FileTree
              repos={repos}
              selected={selectedFile}
              onSelect={scrollToFile}
              filter={filter}
              collapsedRepos={collapsedRepos}
              onToggleRepo={toggleRepo}
            />
          </div>

          <div
            ref={portRef}
            onScroll={onScroll}
            className="relative h-[72vh] min-h-[24rem] overflow-auto bg-surface"
            role="region"
            aria-label="Diff hunks"
            tabIndex={0}
          >
            <div style={{ height: padTop }} aria-hidden />
            {visible.map((row) => (
              <DiffRowView
                key={row.key}
                row={row}
                highlighter={highlighter}
                portWidth={portWidth}
                onOpenRepo={(repoId) => {
                  setIsolatedRepo(repoId);
                  setOnlyFile(null);
                  portRef.current?.scrollTo({ top: 0 });
                }}
              />
            ))}
            <div style={{ height: padBottom }} aria-hidden />
          </div>
        </div>
      )}

      <p className="border-t border-rule px-3 py-2 text-xs leading-[17px] text-ink-3">
        Diffed against <span className="font-mono">{ENVIRONMENT[target].label.toLowerCase()}</span> per repo, so this is
        exactly what {PROMOTION_TARGET[target].verb.toLowerCase()} would merge — not a chat-relative diff. Hunks are
        computed server-side; the rows are virtualised and highlighted in a worker.
      </p>
    </Panel>
  );
}

function TargetSelector({
  target,
  onChange,
}: {
  target: PromotionTarget;
  onChange: (target: PromotionTarget) => void;
}) {
  const options: PromotionTarget[] = ['staging', 'production'];
  return (
    <div
      role="tablist"
      aria-label="Diff target"
      className="flex items-center gap-0.5 rounded-sm border border-rule-strong bg-raised p-0.5"
    >
      {options.map((option) => (
        <button
          key={option}
          type="button"
          role="tab"
          aria-selected={target === option}
          title={PROMOTION_TARGET[option].detail}
          onClick={() => onChange(option)}
          className={cn(
            'rounded-sm px-2 py-0.5 font-mono text-tiny uppercase tracking-[0.08em] transition-colors',
            target === option ? 'bg-sunken text-ink' : 'text-ink-3 hover:text-ink-2',
          )}
        >
          {ENVIRONMENT[option].short}
        </button>
      ))}
    </div>
  );
}

function DiffSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)]">
      <div className="space-y-2 border-r border-rule p-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className={cn('h-3', i % 3 === 0 ? 'w-32' : 'w-24')} />
        ))}
      </div>
      <div className="space-y-[6px] p-3">
        {Array.from({ length: 22 }).map((_, i) => (
          <Skeleton key={i} className="h-3" />
        ))}
      </div>
    </div>
  );
}
